import { IOnboardingDocumentRequest } from '@/@types/Onboarding';
import { toast } from '@/hooks/use-toast';
import { OnboardingService } from '@/services/onboarding.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface UseOnboardingDocumentParams {
  onClose: () => void;
  mode?: 'create' | 'edit';
}

export const useOnboardingDocument = ({
  onClose,
  mode = 'create',
}: UseOnboardingDocumentParams) => {
  const { t } = useTranslation('common');

  const fileValidation = z.custom<FileList>(
    (val) => val instanceof FileList && val.length > 0,
    {
      message: t('required_field'),
    },
  );

  const schema = z.object({
    proof_of_address: fileValidation,
    social_contract:
      mode === 'create'
        ? z
            .any({ message: t('required_field') })
            .refine((file) => file?.[0]?.type === 'application/pdf', {
              message: t('only_pdf_files_allowed'),
            })
            .optional()
        : z.any({ message: t('required_field') }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
  } = useForm<IOnboardingDocumentRequest>({
    shouldUnregister: false,
    resolver: zodResolver(schema),
  });

  const queryClient = useQueryClient();

  const defaultValues = watch();

  const mutationFn =
    mode === 'create'
      ? (data: IOnboardingDocumentRequest) =>
          OnboardingService.sendDocuments(data)
      : (data: IOnboardingDocumentRequest) =>
          OnboardingService.resendDocuments(data);

  const { mutate, isPending: isSendingDocuments } = useMutation({
    mutationFn,
    onSuccess: async () => {
      if (mode === 'edit') {
        await queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      toast({ description: t('data_saved'), variant: 'success' });
      onClose();
    },
    onError: (err: any) => {
      Object.keys(err.response.data.errors).forEach((key: any) => {
        setError(key, {
          type: 'manual',
          message: err.response.data.errors[key],
        });
      });
      toast({ description: err.response.data.message, variant: 'destructive' });
    },
  });

  const onSubmit = (data: IOnboardingDocumentRequest) => {
    data.proof_of_address = data.proof_of_address?.[0];
    data.social_contract = data.social_contract?.[0];
    mutate(data);
  };

  return {
    onSubmit,
    handleSubmit,
    register,
    errors,
    setValue,
    defaultValues,
    watch,
    isSendingDocuments,
  };
};
